<template>
  <b-modal
    id="modal-submit-supplier-order"
    no-close-on-backdrop
    hide-header-close
    hide-footer
    size="lg"
    centered
    @hidden="onHideModal"
  >
    <template #modal-title>
      <p class="h4">
        {{ editMode ? $t('Editar Pedido ao Fornecedor') : $t('Submeter Pedidos ao Fornecedor') }}
      </p>
    </template>

    <FormulateForm
      ref="supplierOrderForm"
      v-model="form"
      name="supplierOrderForm"
    >
      <b-container>
        <FormulateInput
          v-model="form.supplierOrders"
          type="group"
          :label=" $t('Pedidos ao Fornecedor')"
          :repeatable="!editMode"
          :add-label="$t('Adicionar Pedido')"
          remove-position="before"
        >
          <template #default="{ index }">
            <b-row align-h="center">
              <b-col md="6">
                <FormulateInput
                  :id="`requested_date_${index}`"
                  name="requestedDate"
                  class="w-100 required"
                  type="datepicker"
                  validation="required"
                  :label="$t('Data de solicitação')"
                />
              </b-col>

              <b-col md="6">
                <FormulateInput
                  :id="`order_number_${index}`"
                  name="orderNumber"
                  class="w-100"
                  type="text"
                  :label="$t('Número do pedido')"
                />
              </b-col>

              <b-col md="12">
                <e-person-search
                  :id="`supplier_id_${index}`"
                  name="supplierId"
                  class="w-100"
                  is-supplier
                  required
                  :label="$t('Fornecedor')"
                  value-is-object
                  @input="onSupplierSelected($event, index)"
                />
              </b-col>

              <b-col md="12">
                <b-card-actions
                  action-collapse
                  :title="$t('Anexos')"
                >
                  <files-form
                    :files="getOrderFiles(index)"
                    :is-read-only="false"
                    @set-files="(files) => setOrderFiles(index, files)"
                    @remove-file="(file) => removeOrderFile(index, file)"
                  />
                </b-card-actions>
              </b-col>
            </b-row>
          </template>

          <template #remove="{ index: indexItem, removeItem }">
            <div
              v-if="form.supplierOrders && form.supplierOrders.length > 1 && !editMode"
              class="mb-1 position-absolute"
              style="right: 7px; top: 25%;"
            >
              <e-button
                :title="$t('Deletar pedido')"
                icon="trash"
                variant="danger"
                size="sm"
                class="px-0"
                @click="() => removeItem(indexItem)"
              />
            </div>
          </template>
        </FormulateInput>

        <b-row class="mt-3">
          <b-col
            class="d-flex justify-content-end"
            style="gap: 10px"
          >
            <e-button
              :text="$t('Voltar')"
              :text-shortcuts="['ESC']"
              variant="danger"
              @click="onHideModal"
            />
            <e-button
              variant="primary"
              type="submit"
              :busy="busy"
              :text="editMode ? $t('Atualizar') : $t('Confirmar')"
              @click="onConfirm"
            />
          </b-col>
        </b-row>
      </b-container>
    </FormulateForm>
  </b-modal>
</template>

<script>
import { BModal, BContainer, BRow, BCol } from 'bootstrap-vue'
import EButton from '@/views/components/EButton.vue'
import EPersonSearch from '@/views/components/inputs/EPersonSearch.vue'
import { formulateHelper } from '@/mixins'
import FilesForm from '@/views/components/forms/FilesForm.vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import axios from '@axios'

const getDefaultSupplierOrder = () => ({
  requestedDate: new Date().toISOString().split('T')[0],
  orderNumber: '',
  supplier: null,
  supplierId: null,
})

export default {
  components: { BModal, BContainer, BRow, BCol, EButton, EPersonSearch, FilesForm, BCardActions },

  mixins: [formulateHelper],

  props: {
    purchaseRequisitionId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      busy: false,
      editMode: false,
      editingOrderIndex: null,
      form: {
        supplierOrders: [getDefaultSupplierOrder()],
      },
      orderFilesMap: {}, // Mapa para armazenar arquivos de cada pedido por índice
    }
  },

  methods: {
    getOrderFiles(index) {
      if (!this.orderFilesMap[index]) {
        this.$set(this.orderFilesMap, index, [])
      }
      return this.orderFilesMap[index]
    },

    setOrderFiles(index, files) {
      this.$set(this.orderFilesMap, index, files)
    },

    removeOrderFile(index, file) {
      const files = this.getOrderFiles(index)
      const fileIndex = files.findIndex(f => f.id === file.id)
      if (fileIndex > -1) {
        files.splice(fileIndex, 1)
      }
    },

    onSupplierSelected(supplier, index) {
      if (supplier) {
        this.form.supplierOrders[index].supplier = `${supplier.id} - ${supplier.name || supplier.companyName}`
      } else {
        this.form.supplierOrders[index].supplier = null
      }
    },

    async onConfirm() {
      try {
        this.$refs.supplierOrderForm.showErrors()
        if (this.$refs.supplierOrderForm.hasErrors) {
          this.showInvalidDataMessage()
          return
        }

        this.busy = true

        // Se está em modo edição, só atualizar localmente
        if (this.editMode) {
          this.showSuccess({ message: this.$t('Pedido atualizado com sucesso.') })
          this.onHideModal()
          this.$emit('after-confirm')
          return
        }

        // Modo adição: processar cada pedido
        // eslint-disable-next-line no-plusplus
        for (let index = 0; index < this.form.supplierOrders.length; index++) {
          const order = this.form.supplierOrders[index]
          let attachmentUrls = []

          // Fazer upload dos arquivos do pedido se houver
          // Só considera os itens que realmente têm um arquivo: o grupo repetível sempre renderiza
          // uma linha em branco e a exclusão de um anexo deixa o item sem arquivo. Sem esse filtro
          // o POST era disparado sem nenhum arquivo e a api respondia 400.
          const orderFiles = (this.getOrderFiles(index) || []).filter(f => f.file?.file)
          if (orderFiles.length > 0) {
            try {
              const formData = new FormData()
              formData.append('entityId', this.purchaseRequisitionId)
              formData.append('uploadType', 'PurchaseRequisition')

              orderFiles.forEach(fileItem => {
                formData.append('files', fileItem.file.file)
                formData.append('types', fileItem.type ?? 'Others')
              })

              // eslint-disable-next-line no-await-in-loop
              const { data: uploadResponse } = await axios.post(
                `/api/documents/upload-files`,
                formData
              )

              if (uploadResponse && Array.isArray(uploadResponse)) {
                attachmentUrls = uploadResponse.map(file => file.fileUrl)
                order.attachments = attachmentUrls
              }
            } catch (uploadError) {
              this.showError({
                title: this.$t('Ocorreu um erro ao fazer upload dos anexos'),
                error: uploadError,
              })
              return
            }
          }
        }

        if (this.form.supplierOrders.length) {

          // Salvar cada pedido ao fornecedor
          // eslint-disable-next-line no-await-in-loop
          await axios.put(
            `/api/purchase-requisitions/${this.purchaseRequisitionId}/add-supplier-order`,
            this.form.supplierOrders
            // {
            //   requestedDate: new Date(order.requestedDate).toISOString(),
            //   orderNumber: order.orderNumber || null,
            //   supplierId: order.supplier,
            //   attachments: attachmentUrls,
            // }
          )
        }

        this.showSuccess({ message: this.$t('Pedidos ao fornecedor registrados com sucesso.') })
        this.onHideModal()
        this.$emit('after-confirm')
      } catch (error) {
        this.showError({ error })
      } finally {
        this.busy = false
      }
    },

    resetModal() {
      this.form = {
        supplierOrders: [getDefaultSupplierOrder()],
      }
      this.orderFilesMap = {}
      this.editMode = false
      this.editingOrderIndex = null
    },

    editOrder(index, order) {
      this.editMode = true
      this.editingOrderIndex = index
      this.form = {
        supplierOrders: [{ ...order }],
      }
      // Se houver anexos existentes, carregá-los como files com ID
      if (order.attachments && order.attachments.length > 0) {
        const existingFiles = order.attachments.map((url, idx) => ({
          id: `existing-${idx}`,
          file: {
            name: `Anexo-${idx + 1}`,
            url,
          },
        }))
        this.$set(this.orderFilesMap, 0, existingFiles)
      } else {
        this.$set(this.orderFilesMap, 0, [])
      }
      this.showModal()
    },

    onHideModal() {
      this.$bvModal.hide('modal-submit-supplier-order')
    },

    showModal() {
      this.$bvModal.show('modal-submit-supplier-order')
      this.resetModal()
    },
  },
}
</script>

<style lang="scss" scoped></style>
